import { useState, useMemo, useRef, useEffect } from 'react'
import { notifyFollow } from '../lib/followNotify'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { Avatar as SharedAvatar } from './Avatar'
import { supabase } from '../lib/supabase'
import { useFollows, invalidateFollows } from '../hooks/useFollows'

// ── FriendBubbles ────────────────────────────────────────────────────────────
// Two display modes:
//   - "compact": small stacked avatar circles. Used on ShowCard, rendered
//     INLINE next to the title (not floated over the poster — it used to sit
//     absolute bottom-left on top of the show name's own gradient overlay,
//     which a first-time user reads as "whose face is covering the title?"
//     rather than "friends watching this too" — Jason 2026-09-17). Each avatar
//     is now a real link (tap → their profile) with a desktop hover card
//     (name, shows in common, follow) instead of a plain inert title="".
//   - "detail": full row with avatar, name, and progress info. Used on
//     ShowDetail. Includes ahead/behind tag if myProgress is provided.
//
// Friend shape (from useFriendsWatching / useFriendsOnShow):
//   { id, username, display_name, avatar_url, progress: { season, episode } | null }

function Avatar({ friend, size = 22, ring = 'var(--bg-raised)' }) {
  return <SharedAvatar name={friend.display_name || friend.username} avatarUrl={friend.avatar_url} size={size} ring={ring} />
}

const canHover = () => {
  try { return window.matchMedia('(hover: hover) and (pointer: fine)').matches } catch { return false }
}

// ── FriendHoverCard — desktop-only popover: name, shows in common, follow ────
function FriendHoverCard({ friend, myShowIds, userId, anchor, onMouseEnter, onMouseLeave }) {
  const navigate = useNavigate()
  const { followingIds } = useFollows(userId)
  const [isFollowing, setIsFollowing] = useState(() => followingIds.includes(friend.id))
  const [common, setCommon] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => { setIsFollowing(followingIds.includes(friend.id)) }, [followingIds, friend.id])

  useEffect(() => {
    let cancel = false
    supabase.from('public_watchlist').select('show_id').eq('user_id', friend.id)
      .then(({ data }) => {
        if (cancel) return
        const theirs = new Set((data || []).map(r => r.show_id))
        setCommon([...myShowIds].filter(id => theirs.has(id)).length)
      }, () => { if (!cancel) setCommon(0) })
    return () => { cancel = true }
  }, [friend.id, myShowIds])

  const toggleFollow = async (e) => {
    e.stopPropagation()
    if (!userId || busy) return
    setBusy(true)
    if (isFollowing) {
      await supabase.from('follows').delete().eq('follower_id', userId).eq('following_id', friend.id)
      setIsFollowing(false)
    } else {
      await supabase.from('follows').insert({ follower_id: userId, following_id: friend.id })
      setIsFollowing(true)
      notifyFollow(friend.id)
    }
    invalidateFollows(userId)
    setBusy(false)
  }

  return createPortal(
    <div
      onClick={e => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'fixed', left: anchor.x, top: anchor.y, transform: 'translate(-50%, calc(-100% - 10px))',
        zIndex: 'var(--z-tooltip, 999)', width: 190,
        background: 'var(--sheet)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)', padding: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
        fontFamily: 'var(--font)',
      }}
    >
      <div onClick={() => navigate('/u/' + friend.username)} style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
        <Avatar friend={friend} size={30} ring="transparent" />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {friend.display_name || friend.username}
          </div>
          {friend.username && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>@{friend.username}</div>}
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 9 }}>
        {common == null ? 'Loading…' : `${common} show${common === 1 ? '' : 's'} in common`}
      </div>
      {userId && userId !== friend.id && (
        <button onClick={toggleFollow} disabled={busy} style={{
          marginTop: 9, width: '100%', height: 28, borderRadius: 'var(--radius-pill)',
          border: isFollowing ? '1px solid var(--border)' : 'none',
          background: isFollowing ? 'transparent' : 'var(--social)',
          color: isFollowing ? 'var(--text-dim)' : 'var(--social-ink)',
          fontFamily: 'var(--font)', fontSize: 12, fontWeight: 700, cursor: busy ? 'default' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}>{isFollowing ? 'Following' : 'Follow'}</button>
      )}
    </div>,
    document.body
  )
}

// ── Compact stack — for ShowCard, rendered inline by the caller ─────────────
export function FriendBubblesCompact({ friends, size = 18, wl, userId }) {
  const navigate = useNavigate()
  const [hoverFriend, setHoverFriend] = useState(null)
  const [anchor, setAnchor] = useState(null)
  const closeTimer = useRef(null)
  const myShowIds = useMemo(() => new Set((wl?.items || []).map(s => s.id)), [wl?.items])

  if (!friends?.length) return null
  const visible = friends.slice(0, 3)
  const extra = friends.length - visible.length

  const openHover = (friend, el) => {
    if (!canHover()) return
    clearTimeout(closeTimer.current)
    const r = el.getBoundingClientRect()
    setAnchor({ x: r.left + r.width / 2, y: r.top })
    setHoverFriend(friend)
  }
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setHoverFriend(null), 140) }
  const keepOpen = () => clearTimeout(closeTimer.current)

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
      {visible.map((f, i) => (
        <button key={f.id}
          onClick={(e) => { e.stopPropagation(); navigate('/u/' + f.username) }}
          onMouseEnter={(e) => openHover(f, e.currentTarget)}
          onMouseLeave={scheduleClose}
          aria-label={`Open ${f.display_name || f.username}'s profile — watching this too`}
          style={{
            marginLeft: i === 0 ? 0 : -7, padding: 0, border: 'none', background: 'none',
            cursor: 'pointer', borderRadius: 'var(--radius-full)', lineHeight: 0,
            position: 'relative', zIndex: visible.length - i,
          }}
        >
          <Avatar friend={f} size={size} ring="var(--bg-raised)" />
        </button>
      ))}
      {extra > 0 && (
        <div style={{
          marginLeft: -7,
          minWidth: size, height: size, borderRadius: 'var(--radius-full)',
          background: 'rgba(var(--bg-rgb),0.9)',
          border: '1.5px solid var(--bg-raised)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-strong)', fontSize: Math.floor(size * 0.42), fontWeight: 700,
          padding: '0 4px',
          fontFamily: 'var(--font)',
        }}>
          +{extra}
        </div>
      )}
      {hoverFriend && anchor && (
        <FriendHoverCard friend={hoverFriend} myShowIds={myShowIds} userId={userId} anchor={anchor}
          onMouseEnter={keepOpen} onMouseLeave={scheduleClose} />
      )}
    </div>
  )
}

// Convert progress {season, episode} to a flat episode-rank for comparison
// across seasons. We don't have episode-counts for every season available
// here, so we approximate with 100 episodes per season — fine for the
// "ahead/behind" relative comparison (precision doesn't matter; direction does).
const SEASON_WEIGHT = 100
function progressRank(p) {
  if (!p?.season) return 0
  return (p.season - 1) * SEASON_WEIGHT + (p.episode || 0)
}

function aheadBehindLabel(theirRank, myRank) {
  if (!myRank) return null
  const diff = theirRank - myRank
  if (Math.abs(diff) < 1) return { text: 'in sync', color: 'var(--purple)' }
  if (diff > 0)            return { text: `${diff}+ ahead`, color: 'var(--blue-soft)' }
  return                          { text: `${-diff} behind`, color: 'var(--text-dim)' }
}

// ── Detail row — for ShowDetail ──────────────────────────────────────────────
// Renders a "Watching with you" section with each friend's progress.
// myProgress (optional) enables the ahead/behind tag.
export function FriendBubblesDetail({ friends, myProgress, onTapFriend }) {
  const myRank = useMemo(() => progressRank(myProgress), [myProgress])

  if (!friends?.length) return null

  return (
    <div style={{
      marginBottom: 14,
      background: 'color-mix(in srgb, var(--accent) 6%, transparent)',
      border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)',
      borderRadius:'var(--radius-md)',
      padding: '12px 14px',
    }}>
      <div style={{
        color: 'color-mix(in srgb, var(--accent) 80%, transparent)',
        fontSize: 10, fontWeight: 700,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        marginBottom: 10,
        fontFamily: 'var(--font)',
      }}>
        ◉ Watching with you
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {friends.slice(0, 5).map(f => {
          const theirRank = progressRank(f.progress)
          const ab = aheadBehindLabel(theirRank, myRank)
          const epLabel = f.progress?.season
            ? `S${f.progress.season}·E${f.progress.episode || 1}`
            : 'Just starting'
          const handleClick = onTapFriend ? () => onTapFriend(f) : undefined
          return (
            <div
              key={f.id}
              onClick={handleClick}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                cursor: handleClick ? 'pointer' : 'default',
                padding: '4px 0',
              }}
            >
              <Avatar friend={f} size={32} ring="transparent" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: 'var(--text-strong)', fontWeight: 600, fontSize: 14,
                  fontFamily: 'var(--font)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {f.display_name || f.username}
                </div>
                <div style={{
                  color: 'var(--text-dim)', fontSize: 12, marginTop: 1,
                }}>
                  {epLabel}{f.status === 'rewatching' ? ' · rewatch' : ''}
                </div>
              </div>
              {ab && (
                <div style={{
                  color: ab.color, fontSize: 11, fontWeight: 600,
                  background: 'var(--bg-card)',
                  border: `1px solid ${ab.color}33`,
                  borderRadius:'var(--radius-xs)', padding: '4px 8px',
                  fontFamily: 'var(--font)',
                  whiteSpace: 'nowrap',
                }}>
                  {ab.text}
                </div>
              )}
            </div>
          )
        })}
        {friends.length > 5 && (
          <div style={{
            color: 'var(--text-dim)', fontSize: 11, marginTop: 2,
            paddingLeft: 42, fontFamily: 'var(--font)',
          }}>
            +{friends.length - 5} more
          </div>
        )}
      </div>
    </div>
  )
}
